// @greenlight-spec: 59-platform-agent.md#integration-tester
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, FlaskConical, Send, Square } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import {
  EMBEDDED_TEST_SESSION,
  integrationTestTranscript,
  sessionHref,
} from './integrationAgentFixtures';
import {
  LiveStepLine,
  TranscriptRow,
  stepLabel,
  useStreamedTranscript,
  type TranscriptEvent,
} from './Transcript';

/**
 * The Integration tester, embedded where IT is already looking: a live
 * transcript of the dual-route test with Stop, a follow-up composer, and a way
 * out to the full session.
 */
export function AgentTestPane({
  integrationName,
  baseUrl,
  credentialSlug,
  onClose,
}: {
  integrationName: string;
  baseUrl: string;
  credentialSlug: string;
  onClose: () => void;
}): React.JSX.Element {
  const events = React.useMemo(
    () => integrationTestTranscript(integrationName, baseUrl, 'you', credentialSlug),
    [integrationName, baseUrl, credentialSlug],
  );
  const [stopped, setStopped] = React.useState(false);
  const stream = useStreamedTranscript(events, { stepMs: 850, stopped });
  const [extra, setExtra] = React.useState<TranscriptEvent[]>([]);
  const [draft, setDraft] = React.useState('');
  const scrollRef = React.useRef<HTMLOListElement>(null);
  const running = !stream.done && !stopped;

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [stream.visible.length, stream.streamedChars, extra.length]);

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    const n = extra.length;
    setExtra((e) => [
      ...e,
      { id: `u${n}`, kind: 'user', at: new Date().toISOString(), text },
      {
        id: `a${n}`,
        kind: 'assistant',
        at: new Date().toISOString(),
        text: 'On it — re-running the direct route with your note in mind. The report will update in the full session.',
      },
    ]);
    setDraft('');
  };

  const lastId = stream.visible[stream.visible.length - 1]?.id;

  return (
    <section
      className="overflow-hidden rounded-gl-lg border border-border bg-background [contain:inline-size]"
      aria-label="Greenlight Agent test"
      data-testid="agent-test-pane"
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2">
        <span className="flex min-w-0 items-center gap-2">
          <span
            className="inline-flex size-6 shrink-0 items-center justify-center rounded-[8px] bg-glow-indigo text-indigo-ink [&_svg]:size-3.5"
            aria-hidden
          >
            <FlaskConical />
          </span>
          <span className="truncate text-[12.5px] font-semibold">Integration tester</span>
          <span className="text-[11.5px] text-muted-foreground">
            {running ? 'Running' : stopped ? 'Stopped' : 'Finished'}
          </span>
        </span>
        <span className="flex items-center gap-1.5">
          {running ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 px-2 text-[12px]"
              onClick={() => setStopped(true)}
            >
              <Square className="!size-3" aria-hidden /> Stop
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-[12px]"
              onClick={onClose}
            >
              Close
            </Button>
          )}
          <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-[12px] text-link">
            <Link to={sessionHref(EMBEDDED_TEST_SESSION, integrationName)}>
              Open full session <ArrowUpRight className="!size-3" aria-hidden />
            </Link>
          </Button>
        </span>
      </header>
      <p className="border-b border-border px-3 py-1.5 text-[11.5px] text-muted-foreground">
        The direct route hands <span className="font-mono">{credentialSlug}</span>’s raw value to
        the agent for this test; the delivery is recorded in its access ledger.
      </p>

      <ol
        ref={scrollRef}
        className="max-h-[340px] space-y-2.5 overflow-y-auto px-3 py-3"
        aria-label="Transcript"
        aria-live="polite"
      >
        {[...stream.visible, ...extra].map((e) => (
          <li key={e.id}>
            <TranscriptRow
              event={e}
              {...(e.id === lastId && stream.streamedChars !== undefined
                ? { streamedChars: stream.streamedChars }
                : {})}
            />
          </li>
        ))}
        {running && (
          <li>
            <LiveStepLine text={stepLabel(stream.next)} className="text-muted-foreground" />
          </li>
        )}
        {stopped && !stream.done && (
          <li className="font-mono text-[11.5px] text-muted-foreground">
            Stopped by you. The partial transcript is saved with the session.
          </li>
        )}
      </ol>

      {/* A div, not a form: the pane renders inside the Register dialog's form. */}
      <div className="flex items-center gap-2 border-t border-border px-3 py-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              send();
            }
          }}
          placeholder={running ? 'Steer the test…' : 'Ask a follow-up…'}
          aria-label="Follow-up to the Integration tester"
          className="h-8 text-[13px]"
        />
        <Button
          type="button"
          size="icon-sm"
          variant="outline"
          aria-label="Send"
          disabled={!draft.trim()}
          onClick={send}
        >
          <Send aria-hidden />
        </Button>
      </div>
    </section>
  );
}
